import {
  ActionCard,
  BackButton,
  Container,
  CopyCard,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import React, { FunctionComponent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { readApplication } from '../../common/api/application';
import docs from '../../common/assets/svg/docs.svg';
import download from '../../common/assets/svg/download.svg';
import { AppType } from '../../common/enums';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';

const getTooltipContent = (toolTipStr: string) => {
  return <div className="w-52">{toolTipStr}</div>;
};
const AppInfoContainer: FunctionComponent<{
  children: ReactNode;
  tooltipInfo: string;
  heading: string;
}> = ({ children, tooltipInfo: tooltip, heading }) => {
  return (
    <Container className="gap-y-3 flex flex-col" size="medium">
      <span data-tip={tooltip}>{heading}</span>
      <ReactTooltip
        bodyMode={true}
        effect="solid"
        getContent={getTooltipContent}
        multiline={true}
        place="top"
        resizeHide={true}
        scrollHide={true}
      />
      {children}
    </Container>
  );
};

export const ApplicationCredentials: React.FunctionComponent = () => {
  const { applicationId } = useParams();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [application, setApplication] = React.useState<Application>();

  React.useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      const getApplication = async () => {
        const app = await readApplication(
          currentOrg.id,
          currentTeam.id,
          applicationId
        );

        if (app) {
          setApplication(app);
        }
      };
      getApplication();
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  if (
    !application ||
    !currentOrg ||
    !currentTenant ||
    currentTenant.orgId != currentOrg.id
  ) {
    return null;
  }

  const {
    listingId,
    listingName,
    clientSecret,
    appType,
    clientKey,
    serviceKey,
  } = application;

  const { uniqueCode, dnsEntry } = currentTenant;

  return (
    <Page className="flex-col items-center">
      <Container
        className="mb-os1-spacing flex flex-col justify-start gap-6"
        size="medium"
      >
        <div className="flex flex-row items-center gap-x-3">
          <BackButton />
          <PageTitle>{listingName}</PageTitle>
        </div>
        {applicationId && <CopyCard label="App ID" value={applicationId} />}
        {listingId && <CopyCard label="Listing ID" value={listingId} />}

        <div className="flex gap-x-6">
          <AppInfoContainer
            heading="Service"
            tooltipInfo="Use this key and secret within your backend/service implementation"
          >
            <CopyCard label="Client ID" value={serviceKey} />
            <CopyCard
              isSecret={true}
              label="Client Secret"
              value={clientSecret}
            />
          </AppInfoContainer>
          {(appType === AppType.mobile || appType === AppType.web) && (
            <AppInfoContainer
              heading="Front End (Web/Mobile)"
              tooltipInfo="Use this key in your front end app implementation"
            >
              <CopyCard label="Client ID" value={clientKey} />
            </AppInfoContainer>
          )}
        </div>
        <CopyCard label="Tenant ID" value={uniqueCode} />
        <CopyCard label="Tenant URL" value={`https://${dnsEntry}`} />
      </Container>
      <Container size="medium">
        <div className="pb-os1-spacing">
          <PageTitle>Next Steps</PageTitle>
        </div>
        <div className="flex flex-row items-center gap-x-6">
          <ActionCard
            actionTitle="Docs"
            action_test_id="learn-more-btn"
            className="text-sm text-center px-1"
            description="Start development by using the Client ID and Secret."
            direction="VERTICAL"
            iconSource={docs}
            onActionClick={() =>
              window.open(
                'https://docs.getos1.com/getting-started-building-apps#getting-and-using-platform-credentials'
              )
            }
            title="Learn More"
          />
          <ActionCard
            actionTitle="Postman"
            action_test_id="postman-collection-btn"
            className="text-sm text-center px-1"
            description="Explore our APIs without writing code"
            direction="VERTICAL"
            iconSource={download}
            onActionClick={() =>
              window.open('https://docs.getos1.com/postman-collection')
            }
            title="Postman Collection"
          />
          <ActionCard
            actionTitle="Download app"
            action_test_id="download-app-btn"
            className="text-sm text-center px-1"
            description="Develop faster by referring to our sample apps."
            direction="VERTICAL"
            iconSource={download}
            onActionClick={() =>
              window.open('https://github.com/OS1-Hackathon-2022/tires-app')
            }
            title="Sample Web App"
          />
        </div>
        <div className="py-os1-spacing">
          <PageTitle>Creating Your First Entity</PageTitle>
        </div>
        <div className="flex flex-col gap-y-4 w-128">
          <iframe
            data-testid="quickstart-video"
            height="200"
            src="https://www.youtube.com/embed/axMc7NbUIdQ"
            title="quickstart-video"
            width="400"
          />
          <div className="text-sm w-103">
            <span>
              In this video, we will show you how to use our Postman collection
              to create your first Entity.
            </span>
          </div>
        </div>
      </Container>
    </Page>
  );
};
